import { Code, ConnectError } from "@connectrpc/connect";

export const CONNECT_ERROR_TITLE_BY_CODE = {
  [Code.Canceled]: "Canceled",
  [Code.Unknown]: "Unknown Error",
  [Code.InvalidArgument]: "Invalid Argument",
  [Code.DeadlineExceeded]: "Deadline Exceeded",
  [Code.NotFound]: "Not Found",
  [Code.AlreadyExists]: "Already Exists",
  [Code.PermissionDenied]: "Permission Denied",
  [Code.ResourceExhausted]: "Resource Exhausted",
  [Code.FailedPrecondition]: "Failed Precondition",
  [Code.Aborted]: "Aborted",
  [Code.OutOfRange]: "Out of Range",
  [Code.Unimplemented]: "Unimplemented",
  [Code.Internal]: "Internal Error",
  [Code.Unavailable]: "Unavailable",
  [Code.DataLoss]: "Data Loss",
  [Code.Unauthenticated]: "Unauthenticated",
} satisfies Record<Code, string>;

export const PERMISSION_DENIED_ERROR_TITLE = CONNECT_ERROR_TITLE_BY_CODE[Code.PermissionDenied];

export function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

export function isPermissionDeniedConnectError(error: ConnectError | null | undefined): boolean {
  return error?.code === Code.PermissionDenied;
}

export function isNotFoundConnectError(error: ConnectError | null | undefined): boolean {
  return error?.code === Code.NotFound;
}

// Response-metadata header + value the Go API server sets on a "notebook kernel
// is still starting" error. Must match MetaKey and Provisioning in
// go-api-server/notebook/notebookkernelstate/notebookkernelstate.go.
// Header names are case-insensitive and ConnectError.metadata is a Headers
// object, so any casing resolves. The backend intentionally sends a value (not
// just header presence) so future kernel states stay distinguishable.
export const NOTEBOOK_KERNEL_STATE_METADATA_KEY = "chalk-notebook-kernel-state";
export const NOTEBOOK_KERNEL_STATE_PROVISIONING = "PROVISIONING";

// A kernel-dependent RPC fails with CodeUnavailable + this marker while the
// notebook kernel is provisioning (booting, or not started until the first run).
// That is a "keep waiting / keep polling" signal, not a real failure: callers
// should render a calm "Starting kernel…" state and rely on their existing
// retry/poll instead of showing an error box, toast, or a 503/404. A crashed
// kernel or a genuinely-missing notebook returns a different code and is NOT
// matched here, so real errors still surface.
export function isNotebookKernelProvisioning(error: unknown): boolean {
  return (
    error instanceof ConnectError &&
    error.code === Code.Unavailable &&
    error.metadata.get(NOTEBOOK_KERNEL_STATE_METADATA_KEY) === NOTEBOOK_KERNEL_STATE_PROVISIONING
  );
}

// Response-metadata header the Go API server sets on a quota admission rejection (a
// ResourceExhausted error). Must match MetaKey in
// go-api-server/quota/quotaexceeded/quotaexceeded.go. The value is the quota resource key; the
// server also sends the limit, in-use and requested base-unit amounts as chalk-quota-limit,
// chalk-quota-in-use and chalk-quota-requested, and writes the error message from them.
export const QUOTA_EXCEEDED_METADATA_KEY = "chalk-quota-exceeded";

export function isQuotaExceededConnectError(error: ConnectError | null | undefined): boolean {
  return (
    error?.code === Code.ResourceExhausted &&
    error.metadata.get(QUOTA_EXCEEDED_METADATA_KEY) != null
  );
}

export function getConnectErrorDisplayTitle<T>(error: unknown, fallbackTitle: T): T | string {
  if (error == null) {
    return fallbackTitle;
  }
  const connectError = ConnectError.from(error);
  // A quota rejection is the environment's own limit, not a service outage.
  return isQuotaExceededConnectError(connectError)
    ? "Quota exceeded"
    : CONNECT_ERROR_TITLE_BY_CODE[connectError.code];
}

export const DELETED_USER_MESSAGE =
  "Error loading data for current user. Please try logging out and trying again.";

export function isDeletedUserError(error: unknown): boolean {
  return ConnectError.from(error).rawMessage.includes(
    "failed to get agent from JWT: error while loading the user for this web token",
  );
}

export function formatConnectError(error: unknown): string {
  const connectError = ConnectError.from(error);
  const rawMessage = connectError.rawMessage.trim();

  if (isDeletedUserError(connectError)) {
    return DELETED_USER_MESSAGE;
  }

  if (
    rawMessage.toLowerCase().includes("failed to fetch") ||
    connectError.message.toLowerCase().includes("failed to fetch")
  ) {
    return GRPC_API_SERVER_CONNECTIVITY_MESSAGE;
  }
  if (rawMessage.length > 0) {
    return connectError.rawMessage;
  }
  if (isPermissionDeniedConnectError(connectError)) {
    return "Permission denied";
  }
  return CONNECT_ERROR_TITLE_BY_CODE[connectError.code];
}

export function formatMaybeConnectError(error: unknown): string {
  return error != null ? formatConnectError(error) : "";
}

export const GRPC_API_SERVER_CONNECTIVITY_MESSAGE =
  "Failed to fetch. Unable to connect to API server (gRPC). The browser did not receive an HTTP response; check that the API server is running, the configured gRPC host is reachable, and CORS/preflight is allowing the request.";

export function getQuotaSettingsHref(
  error: unknown,
  projectId: string | undefined,
  environmentId: string | undefined,
): string | undefined {
  if (!isQuotaExceededConnectError(ConnectError.from(error)) || !projectId || !environmentId) {
    return undefined;
  }
  return `/projects/${encodeURIComponent(projectId)}/environments/${encodeURIComponent(environmentId)}/settings/quotas`;
}
